import { Box, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";

type TagListProps = Readonly<{
  items: readonly string[];
}>;

const chipSx = {
  display: "inline-block",
  px: "7px",
  py: "1px",
  fontSize: "0.6875rem",
  fontWeight: 500,
  backgroundColor: "var(--bg-raised)",
  color: "var(--text-secondary)",
  border: "1px solid var(--border-mid)",
  borderRadius: "12px",
};

export default function TagList({ items }: TagListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(items.length);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      const chipWidth = 60;
      const moreButtonWidth = 50;
      const fits = Math.floor(width / chipWidth);
      setMaxVisible(
        fits >= items.length
          ? items.length
          : Math.max(1, Math.floor((width - moreButtonWidth) / chipWidth)),
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [items.length]);

  const hidden = items.length - maxVisible;
  const visible = items.slice(0, maxVisible);
  const overflow = items.slice(maxVisible);

  if (items.length === 0) {
    return (
      <Box component="span" sx={{ color: "var(--text-muted)" }}>
        {"\u2014"}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: "4px",
        flexWrap: "nowrap",
        overflow: "hidden",
      }}
      ref={containerRef}
    >
      {visible.map((item, i) => (
        <Box key={`${item}-${i}`} component="span" sx={chipSx}>
          {item}
        </Box>
      ))}
      {hidden > 0 && (
        <Tooltip
          title={overflow.map((item, i) => (
            <Box key={`${item}-${i}`} sx={{ py: "2px" }}>{item}</Box>
          ))}
        >
          <Box component="span" sx={chipSx}>
            {`+${hidden}`}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
